// src/pages/QuickQuote.styles.js
import styled from 'styled-components';
import { motion } from 'framer-motion';

export const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  padding-top: 120px;
  padding-bottom: 140px;
`;

export const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`;

export const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  font-size: 1rem;
  color: #64748b;
`;

export const Title = styled.h1`
  font-size: 2.8rem;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
  font-weight: 700;
`;

export const Subtitle = styled.p`
  color: #64748b;
  font-size: 1.1rem;
  margin-bottom: 2rem;
`;

export const Tabs = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

export const TabButton = styled.div`
  padding: 0.8rem 1.6rem;
  background: ${props => (props.$active ? '#001F3D' : '#e2e8f0')};
  color: ${props => (props.$active ? 'white' : '#64748b')};
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const Card = styled(motion.div)`
  background: white;
  border-radius: 24px;
  padding: 3rem;
  box-shadow: 0 25px 50px rgba(0,0,0,0.1);
  max-width: 1200px;
  margin: 0 auto;
`;

export const FormGroup = styled.div`
  margin-bottom: 1.8rem;
`;

export const Label = styled.label`
  display: block;
  margin-bottom: 0.6rem;
  font-weight: 600;
  color: #1a1a1a;
  font-size: 1rem;
`;

export const Required = styled.span`
  color: #e00;
`;

export const Input = styled.input`
  width: 100%;
  padding: 1.1rem;
  border: 2px solid #e2e8f0;
  border-radius: 14px;
  font-size: 1.05rem;
  background: #fbfbfb;
  transition: all 0.3s;
  
  &:focus {
    outline: none;
    border-color: #001F3D;
    background: white;
    box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.1);
  }
  
  &:disabled {
    background: #f1f5f9;
    cursor: not-allowed;
  }
`;

export const Select = styled.select`
  width: 100%;
  padding: 1.1rem;
  border: 2px solid #e2e8f0;
  border-radius: 14px;
  font-size: 1.05rem;
  background: #fbfbfb;
  
  &:focus {
    outline: none;
    border-color: #001F3D;
  }
  
  &:disabled {
    background: #f1f5f9;
    cursor: not-allowed;
  }
`;

export const SizeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.8rem;
  margin: 2.5rem 0;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const SizeCard = styled.div`
  border: 3px solid ${props => (props.$selected ? '#001F3D' : '#e2e8f0')};
  border-radius: 20px;
  padding: 2.2rem 1.2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => (props.$selected ? '#fff8f5' : 'white')};
  box-shadow: 0 8px 20px rgba(0,0,0,0.05);
  
  &:hover {
    transform: translateY(-8px);
    border-color: #001F3D;
    box-shadow: 0 15px 30px rgba(255,107,53,0.15);
  }
`;

export const Price = styled.div`
  font-size: 2.2rem;
  font-weight: bold;
  color: #001F3D;
  margin: 1rem 0;
`;

export const Dimension = styled.p`
  font-size: 0.9rem;
  color: #666;
  margin: 0.5rem 0;
  font-weight: 500;
`;

export const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 3rem;
  gap: 1.5rem;
`;

export const Button = styled.button`
  padding: 1.1rem 2.5rem;
  border-radius: 14px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  flex: 1;
  transition: all 0.3s;
  font-family: inherit;
  
  ${props => props.$primary ? `
    background: #001F3D;
    color: white;
    border: none;
  ` : `
    background: transparent;
    color: #1a1a1a;
    border: 2px solid #cbd5e1;
  `}
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0,0,0,0.1);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const SuccessScreenWrapper = styled(motion.div)`
  text-align: center;
  padding: 3rem;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border-radius: 24px;
  border: 3px solid #86efac;
`;

export const SuccessTitle = styled.h2`
  font-size: 2.2rem;
  color: #166534;
  margin-bottom: 1rem;
`;

export const SuccessMessage = styled.p`
  font-size: 1.2rem;
  color: #166534;
  margin-bottom: 1.5rem;
`;

export const ReferenceBox = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 16px;
  display: inline-block;
  margin: 1.5rem 0;
  font-size: 1.4rem;
  font-weight: bold;
  color: #166534;
  border: 2px dashed #86efac;
`;

export const ReferenceCode = styled.span`
  color: #001F3D;
`;

export const AdvancedSection = styled.div`
  margin-top: 2rem;
  padding: 2rem;
  background: #f0f9ff;
  border-radius: 16px;
  border: 2px solid #bae6fd;
`;

export const DateGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const ReviewSection = styled.div`
  background: #f8fafc;
  padding: 2rem;
  border-radius: 20px;
  font-size: 1.1rem;
  line-height: 2;
`;

export const SectionHeader = styled.h2`
  font-size: 1.9rem;
  margin-bottom: 2rem;
  color: #1a1a1a;
  
  svg {
    display: inline;
    margin-right: 12px;
    color: #001F3D;
  }
`;

export const ReviewHeader = styled.h2`
  font-size: 2.1rem;
  text-align: center;
  margin-bottom: 2rem;
  color: #1a1a1a;
`;

export const TotalPrice = styled.span`
  font-size: 2rem;
  color: #001F3D;
  font-weight: bold;
`;

export const Divider = styled.hr`
  margin: 1.5rem 0;
  border: 1px dashed #ddd;
`;

// New styled components for LWB van pricing
export const PricingCard = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 20px;
  padding: 2rem;
  margin: 2rem 0;
  border: 2px solid #e2e8f0;
`;

export const PriceBreakdown = styled.div`
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  margin: 1.5rem 0;
  border: 1px solid #e2e8f0;
`;

export const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f1f5f9;
  
  &:last-child {
    border-bottom: none;
    font-weight: bold;
    font-size: 1.2rem;
    padding-top: 1rem;
    border-top: 2px solid #e2e8f0;
  }
`;

export const LoadTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin: 1.5rem 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const LoadTypeCard = styled.div`
  border: 2px solid ${props => props.$selected ? '#001F3D' : '#e2e8f0'};
  border-radius: 12px;
  padding: 1.5rem;
  cursor: pointer;
  background: ${props => props.$selected ? '#fff8f5' : 'white'};
  transition: all 0.3s;
  
  &:hover {
    border-color: #001F3D;
    transform: translateY(-2px);
  }
`;

export const TimeSlotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin: 1.5rem 0;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const TimeSlot = styled.div`
  border: 2px solid ${props => props.$selected ? '#001F3D' : '#e2e8f0'};
  border-radius: 12px;
  padding: 1rem;
  text-align: center;
  cursor: pointer;
  background: ${props => props.$selected ? '#fff8f5' : 'white'};
  transition: all 0.3s;
  
  &:hover {
    border-color: #001F3D;
  }
`;

export const UrgencyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin: 1.5rem 0;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const UrgencyCard = styled.div`
  border: 2px solid ${props => props.$selected ? '#001F3D' : '#e2e8f0'};
  border-radius: 16px;
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  background: ${props => props.$selected ? '#fff8f5' : 'white'};
  transition: all 0.3s;
  
  &:hover {
    border-color: #001F3D;
    transform: translateY(-4px);
  }
`;

export const WarningBox = styled.div`
  background: #fffbeb;
  border: 2px solid #fbbf24;
  border-radius: 12px;
  padding: 1rem 1.5rem;
  margin: 1rem 0;
  color: #92400e;
  
  strong {
    color: #92400e;
  }
`;

export const ErrorBox = styled.div`
  background: #fef2f2;
  border: 2px solid #f87171;
  border-radius: 12px;
  padding: 1rem 1.5rem;
  margin: 1rem 0;
  color: #991b1b;
  
  strong {
    color: #991b1b;
  }
`;

export const InfoBox = styled.div`
  background: #f0f9ff;
  border: 2px solid #7dd3fc;
  border-radius: 12px;
  padding: 1rem 1.5rem;
  margin: 1rem 0;
  color: #0c4a6e;
`;

export const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #001F3D;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;